import React, { Fragment, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { unescape, get } from 'helpers/utilities';
import EventHandler from 'helpers/EventHandler';
import { EVENT } from 'config/constants';
import { slugify, truncateByWord } from 'helpers/text';
import useThrottle from '../../hooks/useThrottle';
import useBreakpoint from '../../hooks/useBreakpoint';
import useTimeout from '../../hooks/useTimeout';

import styles from './styles.scss';

const totalNavOffsets = {
  mobile: 148,
  tablet: 200,
  smallDesktop: 150,
  largeDesktop: 150
};

const OffsetDist = {
  mobile: 5,
  tablet: 30,
  smallDesktop: 10,
  largeDesktop: 10
};

const JobCardGrid = props => {
  const {
    currentID,
    location,
    setJobLocation,
    clicked,
    setClick,
    initLocations,
    careersRef,
    setSlide,
    setFixedNavigation,
    navToggling,
    jobsData
  } = props;

  const jobsLocationRef = useRef([]);
  const lastScrollTop = useRef(0);
  const offsetFrom = useRef(0);
  const offsetDist = useRef(0);
  const origTime = useRef(500);
  const initialTime = useRef(0);
  const breakpoint = useBreakpoint();
  const throttle = useThrottle();
  const timeout = useTimeout();

  // componentDidMount
  useEffect(() => {
    function scrollHandler() {
      const { top, bottom } = careersRef.current.getBoundingClientRect();
      if (top <= 0 && bottom >= 0 && navToggling) {
        // setNavToFixed, navToggling
        timeout(() => {
          setFixedNavigation(true, false, false);
        });
      } else if (((top > 0 && bottom > 0) || (top < 0 && bottom < 0)) && !navToggling) {
        setFixedNavigation(false, true);
      }

      if (clicked) return;
      const index = initLocations.indexOf(location);
      const getBottom = jobsLocationRef.current[index]?.getBoundingClientRect().bottom;
      const getTop = jobsLocationRef.current[index]?.getBoundingClientRect().top;
      const getTopCareers = careersRef.current.getBoundingClientRect().top;
      const st = window.pageYOffset || document.documentElement.scrollTop;
      const offsetY =
        breakpoint === 'mobile' || breakpoint === 'tablet'
          ? totalNavOffsets.mobile
          : 80 + 70 * index;

      // this.triggerScroll(window.pageYOffset);
      if (st > lastScrollTop.current) {
        if (getBottom < offsetY) {
          // sets the next location
          const nextIndex =
            index + 1 > initLocations.length - 1 ? initLocations.length - 1 : index + 1;
          setJobLocation(initLocations[nextIndex], false);
        }
      }
      if (st < lastScrollTop.current) {
        if (getTop > totalNavOffsets.mobile) {
          // sets the previous location
          const prevIndex = index - 1 > -1 ? index - 1 : 0;
          setJobLocation(initLocations[prevIndex], false);
        }
      }

      if (getTopCareers < 0) {
        setSlide(true);
      } else if (getTopCareers > 0) {
        setSlide(false);
      }

      lastScrollTop.current = st <= 0 ? 0 : st;
    }
    const scrollEvent = EventHandler.subscribe(EVENT.SCROLL, throttle(scrollHandler, 100));

    // Cleanup
    return () => EventHandler.unsubscribe(scrollEvent);
  }, [navToggling, setFixedNavigation, setSlide, setJobLocation, clicked]);

  useEffect(() => {
    if (clicked) {
      locationClicked();
    }
  }, [clicked]);

  useEffect(() => () => setFixedNavigation(true, true), []);

  function getCardStyle(breakpoint, index) {
    const gridRows = {
      largeDesktop: index / 2,
      smallDesktop: index / 2,
      tablet: index / 2 + 1,
      mobile: index + 1
    };
    const msGridRow = Math.floor(gridRows[breakpoint] + 1);
    return { msGridRow };
  }

  function getJobsLocationOffset() {
    const selectedRef = jobsLocationRef.current[currentID];

    // Since the jobCardGrid has a padding-margin special layout for some reason on desktop,
    // We use the first card in the grid to get the offset.
    const firstCard = selectedRef.firstElementChild;
    const overAllCareersOffset = firstCard.getBoundingClientRect().top;

    const navHeight = totalNavOffsets[breakpoint];
    const containerPaddingOffset = /mobile/.test(breakpoint) ? 0 : 60;
    const navOffset = navHeight;

    // we need to set an offset distance of 10px to fix the juggling issue
    const offsetDistance = OffsetDist[breakpoint];
    return overAllCareersOffset - navOffset - containerPaddingOffset + offsetDistance;
  }

  function createURLParam(location) {
    const locVariable = `?location=${slugify(location)}`;
    return locVariable;
  }

  function loopScroll(time) {
    initialTime.current = initialTime.current || time;
    const currTime = time - initialTime.current;
    const dist = distancePerTime(currTime, origTime.current, offsetDist.current);

    if (currTime < origTime.current) {
      window.scrollTo(0, dist + offsetFrom.current);
      window.requestAnimationFrame(loopScroll);
    } else {
      window.scrollTo(0, offsetDist.current + offsetFrom.current);
      setClick(false);
    }
  }

  function distancePerTime(currTime, origTime, offsetDist) {
    const per = currTime / origTime;
    return per * offsetDist;
  }

  function locationClicked() {
    if (!clicked) return;
    if (breakpoint === 'smallDesktop' || breakpoint === 'largeDesktop') {
      setFixedNavigation(true, false);
    }
    offsetFrom.current = window.pageYOffset;
    // Offset Distance = overAllCareersOffset - Current window.pageYOffset -
    // navHeight (This must be the height of Location button container + height of Navbar)
    offsetDist.current = getJobsLocationOffset();

    initialTime.current = 0;

    window.requestAnimationFrame(loopScroll);
  }

  return (
    <>
      {Object.keys(jobsData).map((loc, index) => (
        <section
          key={index}
          className={`${styles.jobCardGrid} ${styles.jobCardGrid}--${slugify(
            initLocations[index]
          )}`}
          ref={_jobsLocationRef => (jobsLocationRef.current[index] = _jobsLocationRef)}>
          {jobsData[loc].map((job, i) => (
            <article
              key={i}
              className={styles.jobCardGrid__jobCard}
              style={getCardStyle(breakpoint, i)}>
              <Link
                to={{
                  pathname: `/careers/${job.id}`,
                  search: createURLParam(get(job, 'offices[0].name'))
                }}
                title={job.title}
                className={`careers-job-link ${
                  i === 0 || i === jobsData[loc].length - 1 ? 'careers-tab' : ''
                } location-${index} careers-tab-${i}`}>
                <span className={styles.jobCardGrid__jobTitle}>{job.title}</span>
              </Link>
              <JobCardDescription description={job.content} />
            </article>
          ))}
        </section>
      ))}
    </>
  );
};

const JobCardDescription = ({ description }) => {
  const descriptionRef = useRef();

  useEffect(() => {
    if (!descriptionRef.current) return;
    descriptionRef.current.innerHTML = unescape(description);
    descriptionRef.current.innerHTML = `${truncateByWord(
      descriptionRef.current.textContent.trim(),
      150
    )}`;
  }, [description]);

  return <div className={styles.jobCardGrid__jobDescription} ref={descriptionRef} />;
};

export default JobCardGrid;
